// src/pages/funnels/Results.jsx
// /results — one screen for how every funnel and landing page is performing.
//
// The per-funnel Stats tab answers "how is this one doing?". This answers
// "which of these is worth my time?" — every surface ranked side by side, with
// the traffic sources and devices behind the numbers.
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { authFetch } from '../../lib/authFetch';
import { Card, CardHeader, CardTitle, CardContent, Badge, Button } from '../../components/ui';
import { formatNumber, cn } from '../../lib/utils';
import { LineChart as LineChartIcon, RefreshCw, Filter, FileText, Eye, Users } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import toast from 'react-hot-toast';

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

const KIND_TABS = [
  { id: 'all', label: 'All' },
  { id: 'funnel', label: 'Funnels' },
  { id: 'page', label: 'Landing Pages' },
];

function StatCard({ label, value, hint }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className="text-2xl font-bold">{value}</p>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      </CardContent>
    </Card>
  );
}

// Horizontal bar list — used for both traffic sources and devices.
function BreakdownList({ title, rows, empty }) {
  const max = Math.max(1, ...rows.map(r => r.views));
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{empty}</p>
        ) : (
          <div className="space-y-2.5">
            {rows.map(r => (
              <div key={r.name}>
                <div className="flex items-baseline justify-between text-sm mb-1 gap-2">
                  <span className="truncate font-medium" title={r.name}>{r.name}</span>
                  <span className="text-muted-foreground text-xs whitespace-nowrap">
                    {formatNumber(r.views)} views · {formatNumber(r.leads)} leads · {r.cvr}%
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${(r.views / max) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Results() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [kind, setKind] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/funnels?resource=results&days=${days}`);
      const json = await res.json();
      if (json.success) setData(json);
      else toast.error(json.error || 'Failed to load results');
    } catch (e) {
      console.error('Results load error:', e);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!data) return <p className="text-sm text-muted-foreground py-8 text-center">Failed to load results.</p>;

  const rows = data.rows.filter(r => kind === 'all' || r.kind === kind);
  // Totals come back for everything; recompute when a kind filter is on so the
  // headline figures always match the table underneath them.
  const totals = kind === 'all'
    ? data.totals
    : rows.reduce((acc, r) => {
        acc.views += r.views; acc.leads += r.leads;
        acc.cvr = acc.views ? Math.round((acc.leads / acc.views) * 1000) / 10 : 0;
        return acc;
      }, { views: 0, leads: 0, cvr: 0 });

  const series = data.daily.map(d => ({
    ...d,
    label: new Date(`${d.day}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
  }));

  const hasAnything = data.rows.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Results</h1>
          <p className="text-sm text-muted-foreground">
            Views, leads and conversion rate across every funnel and landing page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 bg-muted rounded-lg p-0.5">
            {RANGES.map(r => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                className={cn(
                  'px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors',
                  days === r.days ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {data.truncated && (
        <div className="rounded-lg border border-amber-300/50 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm">
          This window has more events than can be summarised in one pass — the figures below are partial.
          Narrow the date range for exact numbers.
        </div>
      )}

      {!hasAnything ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <LineChartIcon className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <h3 className="font-semibold mb-1">Nothing to report yet</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
              Publish a funnel or a landing page and its traffic will show up here.
            </p>
            <div className="flex gap-2">
              <Link to="/funnels"><Button variant="outline"><Filter className="h-4 w-4 mr-2" /> Funnels</Button></Link>
              <Link to="/landing-pages"><Button><FileText className="h-4 w-4 mr-2" /> Landing Pages</Button></Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label={`Views (${days}d)`} value={formatNumber(totals.views)} />
            <StatCard label={`Leads (${days}d)`} value={formatNumber(totals.leads)} />
            <StatCard label="Conversion rate" value={`${totals.cvr}%`} />
            <StatCard
              label="Live"
              value={rows.filter(r => r.status === 'published').length}
              hint={`of ${rows.length} total`}
            />
          </div>

          {series.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Views &amp; leads over time</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="views" name="Views" stroke="#6366f1" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="leads" name="Leads" stroke="#10b981" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-base">By page</CardTitle>
                <div className="flex items-center gap-0.5 bg-muted rounded-lg p-0.5">
                  {KIND_TABS.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setKind(t.id)}
                      className={cn(
                        'px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors',
                        kind === t.id ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      {['Name', 'Type', 'Status', 'Views', 'Leads', 'CVR', 'Lifetime'].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                          Nothing in this category yet.
                        </td>
                      </tr>
                    ) : rows.map(r => (
                      <tr key={r.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-2.5">
                          <Link to={`/funnels/${r.id}`} className="font-medium hover:text-primary transition-colors">
                            {r.name}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                            {r.kind === 'page'
                              ? <><FileText className="h-3.5 w-3.5" /> Page</>
                              : <><Filter className="h-3.5 w-3.5" /> Funnel</>}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge className={cn(
                            'text-[10px] px-1.5 py-0.5',
                            r.status === 'published' ? 'bg-green-500' : 'bg-muted-foreground/60'
                          )}>
                            {r.status === 'published' ? 'Live' : 'Draft'}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">{formatNumber(r.views)}</td>
                        <td className="px-4 py-2.5 font-semibold text-emerald-600">{formatNumber(r.leads)}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{r.cvr}%</td>
                        <td className="px-4 py-2.5 text-muted-foreground text-xs whitespace-nowrap">
                          <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{formatNumber(r.lifetime_views)}</span>
                          <span className="inline-flex items-center gap-1 ml-2"><Users className="h-3 w-3" />{formatNumber(r.lifetime_leads)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <BreakdownList
              title="Traffic sources"
              rows={data.sources}
              empty="No traffic in this window."
            />
            <BreakdownList
              title="Devices"
              rows={data.devices}
              empty="No traffic in this window."
            />
          </div>
        </>
      )}
    </div>
  );
}
